import { useState } from 'react';
import { Send, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { logError } from '@/utils/logger';
import { submitErrorMessage } from '@/utils/submitError';
import { INPUT_CLS, SELECT_CONTENT_CLS, SELECT_ITEM_CLS, LABEL_CLS, ERROR_CLS } from '@/components/brand/BrandKit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const ENQUIRY_TYPES = ["Car Rental: New Booking", "Monthly Rental Enquiry", "Long-Term Leasing", "Chauffeur / Managed Transport", "Bus & Coach Transportation", "Commercial Fleet (Truckline)", "Existing Rental Support", "Roadside Assistance", "Used Vehicle Enquiry", "Corporate Account", "General Enquiry"];
const T = {
  en: {
    enquiryTypes: ENQUIRY_TYPES, emirates: ["Dubai", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain", "Abu Dhabi"], times: ["Morning (9am to 12pm)", "Afternoon (12pm to 4pm)", "Evening (4pm to 7pm)", "Any time"],
    type: 'Enquiry type *', typePh: 'What do you need help with?', name: 'Full name *', namePh: 'Your full name', phone: 'Mobile number *', email: 'Email address *', emailPh: 'your@email.com', company: 'Company name', companyPh: 'Optional',
    time: 'Preferred contact time', timePh: 'Select time', emirate: 'Pickup / service emirate', emiratePh: 'Select emirate', message: 'Message', messagePh: 'Tell us more about your requirement...', submit: 'Submit Enquiry', submitting: 'Submitting...',
    successTitle: 'Enquiry submitted', successBody: 'Thank you. A specialist team member will follow up with the appropriate next steps.', tag: '',
  },
  ar: {
    enquiryTypes: ["تأجير سيارة: حجز جديد", "استفسار تأجير شهري", "تأجير طويل الأجل", "سائق خاص / نقل مُدار", "نقل بالحافلات", "أسطول تجاري (تراكلاين)", "دعم تأجير حالي", "مساعدة على الطريق", "استفسار سيارات مستعملة", "حساب شركات", "استفسار عام"],
    emirates: ["دبي", "الشارقة", "عجمان", "رأس الخيمة", "الفجيرة", "أم القيوين", "أبوظبي"], times: ["صباحاً (٩ - ١٢)", "ظهراً (١٢ - ٤)", "مساءً (٤ - ٧)", "أي وقت"],
    type: 'نوع الاستفسار *', typePh: 'كيف يمكننا مساعدتك؟', name: 'الاسم الكامل *', namePh: 'اسمك الكامل', phone: 'رقم الجوال *', email: 'البريد الإلكتروني *', emailPh: 'your@email.com', company: 'اسم الشركة', companyPh: 'اختياري',
    time: 'وقت التواصل المفضل', timePh: 'اختر الوقت', emirate: 'إمارة الاستلام / الخدمة', emiratePh: 'اختر الإمارة', message: 'الرسالة', messagePh: 'أخبرنا المزيد عن متطلباتك...', submit: 'إرسال الطلب', submitting: 'جارٍ الإرسال...',
    successTitle: 'تم إرسال الطلب', successBody: 'شكراً لك. سيتواصل معك أحد المختصين بالخطوات المناسبة.', tag: '[AR] ',
  },
};

function Field({ label, children }) {
  return <div><span className={LABEL_CLS}>{label}</span>{children}</div>;
}

function SelectField({ testid, value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={INPUT_CLS}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className={SELECT_CONTENT_CLS}>{options.map((o) => <SelectItem key={o} value={o} className={SELECT_ITEM_CLS}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function buildContactMessage(form, tag) {
  const time = form.preferred_time ? ` | Preferred contact: ${form.preferred_time}` : '';
  const emirate = form.emirate ? ` | Emirate: ${form.emirate}` : '';
  return `${tag}[${form.enquiry_type}] ${form.message || ''}${time}${emirate}`;
}

export function ContactForm({ defaultType = '', lang = 'en' }) {
  const t = T[lang] || T.en;
  const isAr = lang === 'ar';
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ enquiry_type: defaultType, name: '', phone: '', email: '', company: '', preferred_time: '', emirate: '', message: '' });
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const ready = form.name && form.phone && form.email && form.enquiry_type;

  const handleSubmit = async () => {
    if (!ready) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await axios.post(`${API}/contact`, {
        full_name: form.name, phone: form.phone, email: form.email, company: form.company || '', enquiry_type: form.enquiry_type,
        message: buildContactMessage(form, t.tag),
      });
      setSubmitted(true);
    } catch (err) {
      logError('ContactForm', err);
      setSubmitError(submitErrorMessage(err, isAr));
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div data-testid="contact-success" className="rounded-2xl border border-[#EE5A01]/30 bg-white p-10 text-center shadow-sm">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#EE5A01] text-white"><Check className="w-8 h-8" /></span>
        <h3 className="mt-5 font-heading font-black text-2xl text-[#121212]">{t.successTitle}</h3>
        <p className="mt-2 font-body text-sm text-[#666666]">{t.successBody}</p>
      </div>
    );
  }

  return (
    <div data-testid="contact-form" className="rounded-2xl border border-black/5 bg-white p-6 sm:p-8 shadow-[0_16px_40px_rgba(0,0,0,0.06)] space-y-5">
      <Field label={t.type}><SelectField testid="enquiry-type-select" value={form.enquiry_type} onChange={set('enquiry_type')} placeholder={t.typePh} options={t.enquiryTypes} /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t.name}><Input data-testid="contact-name" value={form.name} onChange={set('name')} placeholder={t.namePh} className={INPUT_CLS} /></Field>
        <Field label={t.phone}><Input data-testid="contact-phone" value={form.phone} onChange={set('phone')} placeholder="+971 XX XXX XXXX" className={INPUT_CLS} dir="ltr" /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t.email}><Input data-testid="contact-email" type="email" value={form.email} onChange={set('email')} placeholder={t.emailPh} className={INPUT_CLS} dir="ltr" /></Field>
        <Field label={t.company}><Input data-testid="contact-company" value={form.company} onChange={set('company')} placeholder={t.companyPh} className={INPUT_CLS} /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t.time}><SelectField testid="contact-time" value={form.preferred_time} onChange={set('preferred_time')} placeholder={t.timePh} options={t.times} /></Field>
        <Field label={t.emirate}><SelectField testid="contact-emirate" value={form.emirate} onChange={set('emirate')} placeholder={t.emiratePh} options={t.emirates} /></Field>
      </div>
      <Field label={t.message}><Textarea data-testid="contact-message" value={form.message} onChange={set('message')} placeholder={t.messagePh} className={`${INPUT_CLS} h-auto min-h-[110px]`} /></Field>
      {submitError && <p data-testid="contact-submit-error" role="alert" className={ERROR_CLS}>{submitError}</p>}
      <button data-testid="contact-submit-btn" onClick={handleSubmit} disabled={submitting || !ready} className="btn-primary w-full disabled:opacity-50">
        <Send className="w-4 h-4" /> {submitting ? t.submitting : t.submit}
      </button>
    </div>
  );
}
